import { createApi } from '@reduxjs/toolkit/query/react'
import ApiMiddleWare from '../api-middleware';
// START-CodeLeap [02-Sep-2026]: [#18188866946] this store carries the prompt-run upload
// (askGPTBlue), so it needs the pre-send attachment probe. It used to declare its own private
// fetchBaseQuery with exactly the config baseQuery.ts already exports, so the two now share one.
//
// [04-Sep-2026]: [PR-2615 review fixes] Same reasoning as secure.ts: baseQueryWithAttachmentProbe,
// not baseQueryWithHttpStatus. The probe is inert for non-multipart requests, so only askGPTBlue's
// upload path changes; every other endpoint in this store behaves exactly as before.
import { baseQueryWithAttachmentProbe } from '../services/baseQuery';
// /END-CodeLeap [02-Sep-2026]: [#18188866946]

// Define a service using a base URL and expected endpoints
export const promptsStore = createApi({
  reducerPath: 'promptsStore',
  // START-CodeLeap [02-Sep-2026]: [#18188866946] shared config + attachment probe, was a private
  // fetchBaseQuery. Behaviour for every non-upload endpoint here is unchanged.
  baseQuery: baseQueryWithAttachmentProbe,
  // /END-CodeLeap [02-Sep-2026]: [#18188866946]
  endpoints: (builder) => ({
    CreatePrompt: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/addGptBluePrompt',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    getPrompts: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getGPTPromptDetail/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    versionHistory: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getGPTBluePromptVersionHistory/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    copyPrompts: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/copyGptBluePrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    setAutonomousTrigger: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/setAutonomousTrigger/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    promptStats: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getGPTBluePromptStats/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    promptTeam: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getGPTPromptTeam/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    updatePrompts: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/changeGptBluePromptStatus/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    askGPTTest: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/testGptBlueFramedPrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    publicAskGPT: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePublicCFC/askGPTBlue/',
          method: 'POST',
          // START-CodeLeap [24-Aug-2026]: [#18188866946] pass FormData straight through so the
          // browser sets the multipart boundary; spreading it would produce an empty body
          body: body instanceof FormData ? body : {
            ...body
          }
          // /END-CodeLeap [24-Aug-2026]: [#18188866946]
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    askGPTRefineResultTest: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/testGptBlueRepetitiveFramedPrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    publicAskGPTRefineResult: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePublicCFC/askGPTBlueIterativePrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    promptStatistics: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getGPTBluePromptStats/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    deployPrompts: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/deployGptBluePrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    downloadPrompts: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/downloadGptBluePrompt/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    shareModalData: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/getUserListToShare/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    shareTask: builder.mutation({
      query: (body) => {
        return {
          url: '/gptbluePrompt/gptBlueShareTask/',
          method: 'POST',
          body: {
            ...body
          }
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
    getFunctionDetail: builder.mutation({
      query: (body) => {
        return {
          url: 'gptbluePrompt/getGPTFunctionDetail/',
          method: 'POST',
          body: {...body}
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
     getRoleList: builder.mutation({
      query: (body) => {
        return {
          url: 'gptbluePrompt/getGPTBlueRole/',
          method: 'POST',
          body: {...body}
        }
      },
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => { try { ApiMiddleWare(await queryFulfilled, dispatch) } catch (error: any) { } }
    }),
  }),
});


export const {
  useCreatePromptMutation,
  useGetPromptsMutation,
  useCopyPromptsMutation,
  usePromptStatsMutation,
  usePromptTeamMutation,
  useUpdatePromptsMutation,
  useAskGPTTestMutation,
  useAskGPTRefineResultTestMutation,
  usePromptStatisticsMutation,
  usePublicAskGPTRefineResultMutation,
  usePublicAskGPTMutation,
  useDeployPromptsMutation,
  useDownloadPromptsMutation,
  useShareModalDataMutation,
  useShareTaskMutation,
  useGetFunctionDetailMutation,
  useVersionHistoryMutation,
  useGetRoleListMutation,
  useSetAutonomousTriggerMutation
} = promptsStore;